/** localStorage key for a stable per-browser NullaPad identity. */
const STORAGE_KEY = "nullapad.instanceSecret.v1";

/** Default length for auto-generated instance secrets (24 bytes → 48 hex chars). */
export const NULLAPAD_INSTANCE_SECRET_HEX_LEN = 48;

/** Inclusive bounds for instance / recipient hex ids (share API + local identity). */
export const NULLAPAD_INSTANCE_SECRET_HEX_MIN_LEN = 4;
export const NULLAPAD_INSTANCE_SECRET_HEX_MAX_LEN = 48;

const SECRET_HEX_LEN = NULLAPAD_INSTANCE_SECRET_HEX_LEN;

function randomHexSecret(): string {
  const buf = new Uint8Array(SECRET_HEX_LEN / 2);
  crypto.getRandomValues(buf);
  return [...buf].map((b) => b.toString(16).padStart(2, "0")).join("");
}

let sessionFallback: string | null = null;

function validStoredSecret(s: string | null): s is string {
  return typeof s === "string" && /^[0-9a-f]{4,48}$/.test(s);
}

/** Trim and lowercase for paste / display normalization before validation or persist. */
export function normalizeNullapadInstanceSecretInput(s: string): string {
  return s.trim().toLowerCase();
}

/** True if `s` is 4–48 hex digits after trim (case-insensitive; stored form is lowercase). */
export function isValidNullapadInstanceSecretFormat(s: string): boolean {
  return typeof s === "string" && /^[0-9a-f]{4,48}$/.test(normalizeNullapadInstanceSecretInput(s));
}

/**
 * Saves a user-chosen instance secret (4–48 lowercase hex chars). Use after editing the title-bar field;
 * share inbox polling uses this id on the server.
 */
export function persistNullapadInstanceSecret(secretHex: string): void {
  const normalized = normalizeNullapadInstanceSecretInput(secretHex);
  if (!/^[0-9a-f]{4,48}$/.test(normalized)) {
    throw new Error(
      `NullaPad instance secret must be ${NULLAPAD_INSTANCE_SECRET_HEX_MIN_LEN}–${NULLAPAD_INSTANCE_SECRET_HEX_MAX_LEN} hexadecimal characters (0-9, a-f).`,
    );
  }
  try {
    localStorage.setItem(STORAGE_KEY, normalized);
    sessionFallback = null;
  } catch {
    throw new Error("Could not save the instance secret (storage blocked).");
  }
}

/**
 * Returns a unique secret for this NullaPad install in the current browser.
 * Persisted in `localStorage` on first use; if storage is unavailable, a
 * per-session value is used instead.
 */
export function getOrCreateNullapadInstanceSecret(): string {
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (validStoredSecret(existing)) return existing;
    const secret = randomHexSecret();
    localStorage.setItem(STORAGE_KEY, secret);
    return secret;
  } catch {
    if (!sessionFallback) sessionFallback = randomHexSecret();
    return sessionFallback;
  }
}
